/**
 * Timeline builder for AgentAssistLiveCard — mirrors LiveAssistCard state runs / frozen past
 * so behaviour stays aligned without importing private LiveAssistCard internals.
 */
import type {
  TurnAnalysisTurn,
  AgentAssistStep,
  StateActionOrderedItem,
  StateActionViolationItem,
} from '../types/api';

export type LiveActionStatus =
  | 'completed'
  | 'completed_proactive'
  | 'completed_mandatory_out_of_order'
  | 'completed_inorder_out_of_order'
  | 'completed_failed'
  | 'live'
  | 'next'
  | 'pending'
  /** Past / optional: left state without mandatory or inorder — no warning icon */
  | 'skipped'
  | 'skipped_mandatory'
  | 'skipped_inorder'
  | 'missed_mandatory'
  | 'missed_inorder';

export interface LiveAssistViolationItem {
  icon: string;
  violationType: string;
  message: string;
  severity?: string;
}

/** Stable key for one ta_turn_action row (instance id, or step + subflow). */
export function orderedRowKeyFromItem(item: {
  step_id?: string;
  canonical_action_name?: string;
  step_text?: string;
  step_instance_id?: string | null;
  subflow_name?: string | null;
}): string {
  const sid = String(item.step_instance_id ?? '').trim();
  if (sid) return sid;
  const step = (item.step_id ?? item.canonical_action_name ?? item.step_text ?? '').trim().toLowerCase();
  const sf = (item.subflow_name ?? '').trim().toLowerCase();
  return `${step}::${sf}`;
}

export interface LiveAssistTimelineAction {
  key: string;
  step: string;
  /** Matches orderedRowKeyFromItem for state_actions_ordered row lookup. */
  rowKey?: string;
  /** Human-readable title (step_title); falls back to formatted step id in UI. */
  displayTitle?: string;
  /** Subflow context for this row (ta_turn_action.subflow_name). */
  subflowName?: string | null;
  /** Script flags e.g. mandatory, inorder — shown as micro badges on the row. */
  scriptConstraints?: string[];
  state?: string;
  status: LiveActionStatus;
  primaryEntityName?: string;
  remark?: string;
  violations?: LiveAssistViolationItem[];
  /** Rich copy from ta_turn_action (timeline expanded body when any set). */
  stepSubtitle?: string | null;
  assistText?: string | null;
  headsUp?: Array<{ type?: string; title?: string; hint?: string }>;
}

/** Deduped lowercase constraint keys from backend. */
export function normalizedScriptConstraints(raw: string[] | undefined | null): string[] {
  if (!raw?.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of raw) {
    const k = String(c).toLowerCase().trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/** Ultra-compact pill text + tooltip for script constraint badges. */
export function filterHeadsUpItems(
  raw: Array<{ type?: string; title?: string; hint?: string }> | null | undefined
): Array<{ type?: string; title?: string; hint?: string }> {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (h) => (h?.title ?? '').trim().length > 0 || (h?.hint ?? '').trim().length > 0
  );
}

/** Rich timeline fields copied from one state_actions_ordered row. */
export function timelineRichFieldsFromOrdered(item: StateActionOrderedItem): {
  stepSubtitle?: string;
  assistText?: string;
  headsUp: Array<{ type?: string; title?: string; hint?: string }>;
  hasRichBody: boolean;
} {
  const stepSubtitle = (item.step_subtitle ?? '').trim() || undefined;
  const assistText = (item.assist_text ?? '').trim() || undefined;
  const headsUp = filterHeadsUpItems(item.heads_up);
  const hasRichBody = Boolean(
    stepSubtitle || assistText || headsUp.length > 0
  );
  return { stepSubtitle, assistText, headsUp, hasRichBody };
}

export function scriptConstraintChipMeta(key: string): { text: string; title: string } {
  const k = key.toLowerCase();
  if (k === 'mandatory' || k === 'mand') return { text: 'M', title: 'Mandatory' };
  if (k === 'inorder' || k === 'in_order' || k === 'order') return { text: 'IO', title: 'In order' };
  const t = key.replace(/_/g, ' ');
  return {
    text: t.length <= 2 ? t.toUpperCase() : t.slice(0, 2).toUpperCase(),
    title: t,
  };
}

export interface LiveAssistTimelineState {
  key: string;
  stateTitle: string;
  turnIndex: number;
}

export interface LiveAssistTimelineSection {
  state: LiveAssistTimelineState;
  actions: LiveAssistTimelineAction[];
}

export interface StateRun {
  stateTitle: string;
  startTurnIndex: number;
  lastTurnIndex: number;
}

function normalizeStepViolations(
  raw: (StateActionViolationItem | string)[] | undefined
): LiveAssistViolationItem[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((v): LiveAssistViolationItem => {
    if (typeof v === 'string') {
      const lower = v.toLowerCase();
      const icon = lower.includes('mandatory') || lower.includes('order') ? '⚠️' : '🚨';
      return { icon, violationType: 'violation', message: v, severity: 'warning' };
    }
    return {
      icon: v.icon ?? '⚠️',
      violationType: v.violation_type ?? 'violation',
      message: v.message ?? '',
      severity: v.severity,
    };
  });
}

function getViolationTargetActionName(v: LiveAssistViolationItem): string | null {
  const m = (v.message ?? '').trim();
  const colon = m.lastIndexOf(':');
  if (colon >= 0) {
    const after = m.slice(colon + 1).trim();
    if (after.length > 0) return after;
  }
  return null;
}

function violationAppliesToAction(v: LiveAssistViolationItem, stepName: string): boolean {
  const target = getViolationTargetActionName(v);
  if (!target) return true;
  return stepName.toLowerCase() === target.toLowerCase();
}

/** Stable key for matching rows across turns (canonical action id). */
function stepKeyNorm(item: StateActionOrderedItem): string {
  return (item.step_id ?? item.canonical_action_name ?? item.step_text ?? '').toLowerCase();
}

function formatSnakeStepLabel(step?: string): string {
  return (
    (step || '')
      .split('_')
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ') || '—'
  );
}

/**
 * Row belongs to the timeline section anchored at ``sectionAnchorStateKey`` (``strict_state.state``).
 * When ``wf_state_name`` is set (per ta_turn_action segment), it must match that id — not the human
 * ``strict_state_title`` used for section headers — so transition turns do not show pre-transition
 * steps under the post-transition state.
 */
export function orderedRowBelongsToSection(
  item: StateActionOrderedItem,
  sectionAnchorStateKey: string | null | undefined
): boolean {
  const wf = item.wf_state_name?.trim();
  if (!wf) return true;
  const key = sectionAnchorStateKey?.trim();
  if (!key) return true;
  return wf === key;
}

const ORDERED_TERMINAL_STATUSES = new Set(['completed', 'failed']);

/** Canonical action id for matching rows across segments / duplicate instances. */
export function canonicalStepKeyFromOrdered(item: StateActionOrderedItem): string {
  return (item.step_id ?? item.canonical_action_name ?? item.step_text ?? '')
    .trim()
    .toLowerCase();
}

/** Mirrors backend ``NON_DIALOGUE_WORKFLOW_ACTION_TYPES`` — agent never speaks these rows. */
export const NON_DIALOGUE_WORKFLOW_ACTION_TYPES = new Set([
  'unset',
  'set',
  'perform',
  'guidance',
]);

const NON_DIALOGUE_STEP_NAME_PREFIXES = ['perform_', 'guidance_', 'unset_', 'set_'] as const;

/** Engine-internal rows that must not drive Focus hero, cursor promotion, or voice dialogue. */
export function isNonDialogueWorkflowStep(
  item: StateActionOrderedItem | undefined
): boolean {
  if (!item) return false;
  const actionType = (item.action_type ?? '').trim().toLowerCase();
  if (actionType && NON_DIALOGUE_WORKFLOW_ACTION_TYPES.has(actionType)) {
    return true;
  }
  const name = canonicalStepKeyFromOrdered(item);
  if (!name) return false;
  return NON_DIALOGUE_STEP_NAME_PREFIXES.some((pfx) => name.startsWith(pfx));
}

/** Dialogue steps eligible for Focus hero and primary cursor display. */
export function isDialogueFocusStep(
  item: StateActionOrderedItem | undefined
): boolean {
  if (!item) return false;
  if (isNonDialogueWorkflowStep(item)) return false;
  return Boolean(canonicalStepKeyFromOrdered(item));
}

/**
 * Rows to drive Focus / Done / Upcoming for the replayed turn.
 * - Anchors to end-of-turn workflow state.
 * - On mid-turn transition, drops departing-state segment rows (e.g. greetings
 *   ask_intent while intent_routing rows are the live script).
 */
export function filterOrderedForLiveLayout(
  ordered: StateActionOrderedItem[] | undefined,
  turn: TurnAnalysisTurn | null | undefined
): StateActionOrderedItem[] {
  if (!ordered?.length) return [];
  const endKey = turn?.strict_state?.state?.trim() || null;
  const startKey = (turn?.start_state ?? '').trim() || null;
  let list = ordered;
  if (endKey) {
    list = list.filter((o) => orderedRowBelongsToSection(o, endKey));
  }
  if (startKey && endKey && startKey !== endKey) {
    list = list.filter((o) => (o.wf_state_name ?? '').trim() !== startKey);
  }
  return list;
}

/** Step ids that still have a non-terminal row in this ordered snapshot. */
export function openStepKeysInOrdered(
  ordered: StateActionOrderedItem[] | undefined
): Set<string> {
  const keys = new Set<string>();
  for (const item of ordered ?? []) {
    const st = (item.status ?? '').toLowerCase();
    if (!ORDERED_TERMINAL_STATUSES.has(st)) {
      const k = canonicalStepKeyFromOrdered(item);
      if (k) keys.add(k);
    }
  }
  return keys;
}

/** Legacy rows without wf_state_name: match human section title to turn snapshots. */
function rowMatchesSectionState(
  item: StateActionOrderedItem,
  sectionStateTitle: string,
  turn: TurnAnalysisTurn
): boolean {
  const wf = item.wf_state_name?.trim();
  if (wf) return wf === sectionStateTitle;
  const strict = turn.strict_state?.state;
  if (strict === sectionStateTitle) return true;
  if (turn.start_state === sectionStateTitle) return true;
  return false;
}

function orderedRowMatchesSectionForResolve(
  o: StateActionOrderedItem,
  sectionStateTitle: string,
  sectionAnchorStateKey: string | null | undefined,
  turn: TurnAnalysisTurn
): boolean {
  if (o.wf_state_name?.trim()) {
    return orderedRowBelongsToSection(o, sectionAnchorStateKey);
  }
  return rowMatchesSectionState(o, sectionStateTitle, turn);
}

/**
 * After a transition, the latest snapshot for a step may live on the next turn under wf_state_name /
 * start_state while buildStateRuns still anchors this section to the last turn before strict_state changed.
 */
export function resolveLatestOrderedItemForSection(
  allTurns: TurnAnalysisTurn[],
  run: StateRun,
  sectionStateTitle: string,
  stepNameNorm: string,
  baseItem: StateActionOrderedItem,
  replayTurnNumber: number
): StateActionOrderedItem {
  let best = baseItem;
  const anchorTurn = allTurns[run.lastTurnIndex];
  const sectionAnchorStateKey = anchorTurn?.strict_state?.state ?? null;
  const startScanIdx = run.lastTurnIndex + 1;
  const endIdx = allTurns.findIndex((x) => x.turn_number === replayTurnNumber);
  if (endIdx < 0 || endIdx < startScanIdx) return best;
  for (let ti = startScanIdx; ti <= endIdx; ti++) {
    const oc = allTurns[ti]?.agent_card?.state_actions_ordered;
    if (!oc) continue;
    const turn = allTurns[ti]!;
    const hit = oc.find(
      (o) =>
        stepKeyNorm(o) === stepNameNorm &&
        orderedRowMatchesSectionForResolve(o, sectionStateTitle, sectionAnchorStateKey, turn)
    );
    if (hit) best = hit;
  }
  return best;
}

const TERMINAL_DB_STATUSES = new Set(['completed', 'failed']);

function scriptConstraintFlags(constraints: string[]): {
  hasMandatory: boolean;
  hasInorder: boolean;
} {
  const lc = constraints.map((c) => String(c).toLowerCase());
  return {
    hasMandatory: lc.includes('mandatory'),
    hasInorder: lc.includes('inorder') || lc.includes('in_order'),
  };
}

/** Engine ``skipped`` or past non-terminal without script constraints. */
function mapOptionalSkipped(flags: ReturnType<typeof scriptConstraintFlags>): LiveActionStatus {
  if (flags.hasMandatory) return 'skipped_mandatory';
  if (flags.hasInorder) return 'skipped_inorder';
  return 'skipped';
}

/** Past state ended before this step finished (mandatory/inorder → missed). */
function mapPastNonTerminal(flags: ReturnType<typeof scriptConstraintFlags>): LiveActionStatus {
  if (flags.hasMandatory || flags.hasInorder) {
    return flags.hasInorder ? 'missed_inorder' : 'missed_mandatory';
  }
  return 'skipped';
}

export function itemStatusToLiveStatus(
  item: StateActionOrderedItem,
  violations: string[],
  isPastState: boolean
): LiveActionStatus {
  const baseStatus = (item.status ?? '').toLowerCase();
  const flags = scriptConstraintFlags((item.constraints ?? []) as string[]);

  if (baseStatus === 'skipped') {
    return mapOptionalSkipped(flags);
  }

  if (baseStatus === 'completed') {
    if (item.is_proactive === true) return 'completed_proactive';
    const b = (item.badge ?? '').toLowerCase();
    if (b.includes('proactive')) return 'completed_proactive';
    if (b.includes('mandatory') && b.includes('order')) return 'completed_mandatory_out_of_order';
    if (b.includes('order')) return 'completed_inorder_out_of_order';
    return 'completed';
  }

  if (baseStatus === 'failed') {
    return 'completed_failed';
  }

  if (baseStatus === 'missed' || baseStatus.startsWith('missed')) {
    return baseStatus.includes('inorder') ? 'missed_inorder' : 'missed_mandatory';
  }

  if (!isPastState) {
    if (
      baseStatus === 'live' ||
      baseStatus === 'awaiting_customer_response' ||
      baseStatus === 'awaiting_confirmation' ||
      baseStatus === 'handle_customer_response'
    ) {
      return 'live';
    }
    if (baseStatus === 'next') return 'next';
    return 'pending';
  }

  if (!TERMINAL_DB_STATUSES.has(baseStatus)) {
    return mapPastNonTerminal(flags);
  }

  return 'pending';
}

const REACHED_STEP_STATUSES = new Set([
  'completed',
  'failed',
  'skipped',
  'missed',
  'missed_mandatory',
  'missed_inorder',
  'awaiting_customer_response',
  'awaiting_confirmation',
  'handle_customer_response',
  'in_progress',
  'live',
  'next',
]);

/** True if this action was ever touched or left not_started within a state run. */
export function stepWasReachedInStateRun(
  allTurns: TurnAnalysisTurn[],
  run: StateRun,
  sectionAnchorStateKey: string | null | undefined,
  stepNorm: string,
  rowKey: string
): boolean {
  for (let ti = run.startTurnIndex; ti <= run.lastTurnIndex; ti++) {
    const ordered = allTurns[ti]?.agent_card?.state_actions_ordered;
    if (!ordered) continue;
    for (const o of ordered) {
      if (!orderedRowBelongsToSection(o, sectionAnchorStateKey)) continue;
      const sn = canonicalStepKeyFromOrdered(o);
      const rk = orderedRowKeyFromItem(o);
      if (sn !== stepNorm && rk !== rowKey) continue;
      if (o.touched_this_turn === true) return true;
      const st = (o.status ?? '').toLowerCase();
      if (REACHED_STEP_STATUSES.has(st)) return true;
    }
  }
  return false;
}

/**
 * When replaying (or viewing) turn T, ``ta_turn.focus_step_id`` is the engine primary cursor.
 * Timeline rows are otherwise ordered by raw step status; this promotes the focused open row
 * to ``live`` so scroll anchors and piles match ``AgentAssistLiveCard`` / DB cursor.
 */
export function promoteFocusStepTimelineStatus(
  mapped: LiveActionStatus,
  item: StateActionOrderedItem,
  opts: { focusStepId: string; isLastSection: boolean }
): LiveActionStatus {
  if (!opts.isLastSection || !opts.focusStepId) return mapped;
  if (isNonDialogueWorkflowStep(item)) return mapped;
  const sid = String(item.step_instance_id ?? '').trim();
  if (!sid || sid !== opts.focusStepId) return mapped;
  if (mapped === 'pending' || mapped === 'next') return 'live';
  return mapped;
}

export function buildStateRuns(allTurns: TurnAnalysisTurn[]): StateRun[] {
  const runs: StateRun[] = [];
  let prevState: string | null = null;
  let runStartTurnIndex = -1;
  let lastTurnIndexForRun = -1;
  allTurns.forEach((t, turnIdx) => {
    const title = t.agent_card?.strict_state_title ?? t.agent_card?.current_state_title ?? null;
    if (!title) return;
    if (title !== prevState) {
      if (prevState !== null) {
        runs.push({ stateTitle: prevState, startTurnIndex: runStartTurnIndex, lastTurnIndex: lastTurnIndexForRun });
      }
      prevState = title;
      runStartTurnIndex = turnIdx;
      lastTurnIndexForRun = turnIdx;
    } else {
      lastTurnIndexForRun = turnIdx;
    }
  });
  if (prevState !== null) {
    runs.push({ stateTitle: prevState, startTurnIndex: runStartTurnIndex, lastTurnIndex: lastTurnIndexForRun });
  }
  return runs;
}

export function buildAssistTimeline(
  allTurns: TurnAnalysisTurn[],
  currentTurnNumber: number
): { sections: LiveAssistTimelineSection[]; actions: LiveAssistTimelineAction[]; runs: StateRun[] } {
  const sections: LiveAssistTimelineSection[] = [];
  const violations: string[] = [];

  const lastTurnNumber = allTurns.length > 0 ? allTurns[allTurns.length - 1].turn_number : 0;
  const waitingForCurrentTurnData = lastTurnNumber < currentTurnNumber;

  const runs = buildStateRuns(allTurns);

  const replayTurnForFocus =
    allTurns.find((x) => x.turn_number === currentTurnNumber) ??
    (allTurns.length > 0 ? allTurns[allTurns.length - 1] : null);
  const focusStepId = (replayTurnForFocus?.focus_step_id ?? '').trim();

  runs.forEach((run, stateIdx) => {
    const stateTitle = run.stateTitle;
    const turnIdx = run.lastTurnIndex;
    const t = allTurns[turnIdx];
    const card = t.agent_card;
    const sectionAnchorStateKey = t.strict_state?.state ?? null;
    const isLastSection = stateIdx === runs.length - 1;
    const isPastState =
      t.turn_number < currentTurnNumber && !(waitingForCurrentTurnData && isLastSection);
    const state: LiveAssistTimelineState = {
      key: `state-${stateTitle}-${stateIdx}-${t.turn_number}`,
      stateTitle,
      turnIndex: t.turn_number,
    };
    const sectionActions: LiveAssistTimelineAction[] = [];
    const ordered = card?.state_actions_ordered;
    const replayIdx = allTurns.findIndex((x) => x.turn_number === currentTurnNumber);
    if (ordered && ordered.length > 0) {
      ordered.forEach((item, actionIdx) => {
        if (!orderedRowBelongsToSection(item, sectionAnchorStateKey)) return;
        const stepName = item.step_id ?? item.canonical_action_name ?? item.step_text ?? '';
        if (!stepName) return;
        const stepNorm = stepName.toLowerCase();
        const itemForStatus =
          replayIdx >= 0 && replayIdx > run.lastTurnIndex
            ? resolveLatestOrderedItemForSection(
                allTurns,
                run,
                stateTitle,
                stepNorm,
                item,
                currentTurnNumber
              )
            : item;
        const displayTitle =
          (itemForStatus.step_title && itemForStatus.step_title.trim()) ||
          formatSnakeStepLabel(stepName);
        const mapped = itemStatusToLiveStatus(itemForStatus, violations, isPastState);
        const status = promoteFocusStepTimelineStatus(mapped, itemForStatus, {
          focusStepId,
          isLastSection,
        });
        const remark = itemForStatus.badge ?? undefined;
        let mergedViolations = normalizeStepViolations(itemForStatus.violations);
        if (run.startTurnIndex < run.lastTurnIndex) {
          const seenKeys = new Set(mergedViolations.map((v) => `${v.violationType}:${v.message}`));
          for (let ti = run.startTurnIndex; ti <= run.lastTurnIndex; ti++) {
            if (ti === turnIdx) continue;
            const oc = allTurns[ti]?.agent_card?.state_actions_ordered;
            if (!oc) continue;
            const other = oc.find(
              (o) =>
                (o.step_id ?? o.canonical_action_name ?? o.step_text ?? '') === stepName &&
                orderedRowBelongsToSection(o, sectionAnchorStateKey)
            );
            if (!other?.violations) continue;
            const normalized = normalizeStepViolations(other.violations);
            for (const v of normalized) {
              const key = `${v.violationType}:${v.message}`;
              if (!seenKeys.has(key)) {
                seenKeys.add(key);
                mergedViolations = mergedViolations.concat(v);
              }
            }
          }
        }
        const filteredViolations = mergedViolations.filter((v) => violationAppliesToAction(v, stepName));
        const rowKey = orderedRowKeyFromItem(itemForStatus);
        const sf = (itemForStatus.subflow_name ?? '').trim();
        const scriptConstraints = normalizedScriptConstraints(itemForStatus.constraints ?? undefined);
        const rich = timelineRichFieldsFromOrdered(itemForStatus);
        sectionActions.push({
          key: `state-${stateTitle}-action-${actionIdx}-${rowKey}`,
          step: stepName,
          rowKey,
          displayTitle,
          subflowName: sf || undefined,
          scriptConstraints: scriptConstraints.length > 0 ? scriptConstraints : undefined,
          primaryEntityName: item.primary_entity_name ?? undefined,
          status,
          remark,
          violations: filteredViolations.length > 0 ? filteredViolations : undefined,
          stepSubtitle: rich.stepSubtitle,
          assistText: rich.assistText,
          headsUp: rich.headsUp.length > 0 ? rich.headsUp : undefined,
        });
      });
    } else if (isLastSection && card) {
      const completedSteps = card.completed_steps ?? [];
      const ongoingSteps = card.ongoing_steps ?? [];
      const upcomingSteps = card.upcoming_steps ?? [];
      const missedSteps = card.missed_steps ?? [];
      const ongoingSet = new Set((ongoingSteps ?? []).map((s) => (s.step ?? '').toLowerCase()));
      const missedSet = new Set((missedSteps ?? []).map((s) => (s.step ?? '').toLowerCase()));
      const seenSteps = new Set<string>();
      const addAction = (step: AgentAssistStep, status: LiveActionStatus, remark?: string) => {
        const stepName = step.step ?? '';
        if (!stepName) return;
        const dedupeKey = `${stepName.toLowerCase()}-${status}`;
        if (seenSteps.has(dedupeKey)) return;
        seenSteps.add(dedupeKey);
        sectionActions.push({
          key: `fallback-${sectionActions.length}-${stepName}`,
          step: stepName,
          displayTitle: formatSnakeStepLabel(stepName),
          state: step.state,
          status,
          remark,
        });
      };
      completedSteps.forEach((s) => addAction(s, 'completed'));
      ongoingSteps.forEach((s) => addAction({ step: s.step, state: s.step }, 'live'));
      upcomingSteps.forEach((s) => {
        const stepName = (s.step ?? '').toLowerCase();
        if (!ongoingSet.has(stepName) && !missedSet.has(stepName)) addAction(s, 'pending');
      });
      missedSteps.forEach((s) => {
        const isMandatory = (violations ?? []).some(
          (v) => v.toLowerCase().includes('mandatory') && v.toLowerCase().includes(s.step ?? '')
        );
        addAction(s, isMandatory ? 'missed_mandatory' : 'missed_inorder', isMandatory ? 'Mandatory missed' : 'In order missed');
      });
    }
    sections.push({ state, actions: sectionActions });
  });

  const actions = sections.flatMap((s) => s.actions);
  return { sections, actions, runs };
}

export function isMissedTypeViolation(v: LiveAssistViolationItem): boolean {
  const t = (v.violationType ?? '').toLowerCase();
  const m = (v.message ?? '').toLowerCase();
  return (
    t.includes('mandatory') ||
    t.includes('order_missed') ||
    t.includes('inorder') ||
    m.includes('mandatory') ||
    m.includes('order missed') ||
    m.includes('in order')
  );
}
